/// <amd-module name="CodeCoverage/AllTab/PathTreeMapper" />
import { ObservableValue, ObservableLike } from "VSS/Core/Observable";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";

import { PathConstants } from "CodeCoverage/ViewCommon/Constants";
import { ICodeCoverageTreeItem } from "CodeCoverage/AllTab/Contracts";

export class PathTreeItemMapper {
    public createPageTree(items: (ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>)[]): ITreeItem<ICodeCoverageTreeItem>[] {
        const root = new PathNode("");
        const createdPageMap: { [key: string]: ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined> } = {};
        // create hierarchy
        for (const item of items) {
            const codeCoverageTreeItem: ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined> = item;
            if (item && !ObservableLike.isObservable(item)) {
                const data = item as ICodeCoverageTreeItem;
                createdPageMap[data.path] = codeCoverageTreeItem;
                this.addItem(root, data.path);
            }
        }
        const treeItems: ITreeItem<ICodeCoverageTreeItem>[] = [];
        if (root.childNodes.length) {
            for (let i = 0; i < root.childNodes.length; ++i) {
                const child = root.childNodes[i];
                this.visit(child, treeItems, "", createdPageMap);
            }
        }
        return treeItems;
    }

    private addItem(root: PathNode, pathName: string): void {
        const result = splitItemPath(pathName);
        let currentNode = root;
        for (const folder of result.folderParts) {
            // keep adding all folders till the last level
            currentNode = this.addItemIfRequired(currentNode, folder);
        }

        if (result.itemName) {
            this.addItemIfRequired(currentNode, result.itemName);
        }
    }

    private addItemIfRequired(parentNode: PathNode, childName: string): PathNode {
        let childNode = parentNode.getChildNode(childName);

        if (!childNode) {
            childNode = parentNode.addChildNode(childName);
        }

        return childNode;
    }

    private visit(
        node: PathNode,
        result: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>>[],
        parentPath: string,
        pageMap: { [key: string]: ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined> }
    ): void {
        const isNotRoot = node.name !== PathConstants.RootPath && node.name !== "";
        const fullFolderName = this.prependParentPath(parentPath, node.name);

        const childrenList = node.childNodes;
        const childItems: ITreeItem<ICodeCoverageTreeItem>[] = [];
        for (let i = 0; i < childrenList.length; ++i) {
            const child = childrenList[i];
            this.visit(child, childItems, fullFolderName, pageMap);
        }
        if (isNotRoot) {
            const item = pageMap[fullFolderName];
            if (item) {
                if (item && !ObservableLike.isObservable(item)) {
                    const data = item as ICodeCoverageTreeItem;
                    const treeItem: ITreeItem<ICodeCoverageTreeItem | ObservableValue<ICodeCoverageTreeItem | undefined>> = {
                        data: data,
                        expanded: data.isDirectory ? true : false,
                        childItems: data.isDirectory ? childItems : undefined
                    };
                    result.push(treeItem);
                }
            }
        } else {
            for (let i = 0; i < childItems.length; ++i) {
                result.push(childItems[i]);
            }
        }
    }

    /**
     * Prepends parentPath to childName. Handles scenarios like no or empty parent path
     */
    private prependParentPath(parentPath: string | null, childName: string): string {
        if (parentPath !== null && parentPath != "") {
            return parentPath + PathConstants.AltPathSeparator + childName;
        }

        return childName;
    }
}

interface SplitResult {
    folderParts: string[];
    itemName: string | undefined;
}

export function splitItemPath(key: string, separator: string = PathConstants.AltPathSeparator): SplitResult {
    const parts = key.split(separator);

    if (parts[parts.length - 1] === "") {
        parts.pop();
    }
    const item = parts.pop();
    return { folderParts: parts, itemName: item };
}

class PathNode {
    private childMap: { [key: string]: PathNode } = {};
    public name: string;

    public childNodes: PathNode[] = [];

    constructor(name: string) {
        this.name = name;
    }

    public getChildNode(name: string): PathNode | undefined {
        return this.childMap[name];
    }

    public addChildNode(name: string): PathNode {
        const childNode = new PathNode(name);
        this.childMap[name] = childNode;
        this.childNodes.push(childNode);

        return childNode;
    }
}
